import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMetadata } from 'contexts/Metadata';
import type { IGenericObject } from 'types';
import { APP_PRODUCT_TYPE } from 'appConstants';
import { displayAddress, displayDate } from 'utils';
import { useOfficeLocation, useTransportMethod } from 'contexts/Metadata/hooks';

export default function useOrderDetailsFieldsCrop(
  transNamespace: string,
  isSummary: boolean = false,
) {
  const { t } = useTranslation(transNamespace, { keyPrefix: 'OrderSummary' });

  const {
    state: { officeLocationsLoading, officeLocationsError, officeLocationsLoadedProductType },
    actions: { loadOfficeLocations },
  } = useMetadata({ loadTransportMethodsData: true });

  const { displayOfficeLocation } = useOfficeLocation();
  const { displayTransportMethod } = useTransportMethod();

  const shouldLoadOfficeLocations =
    isSummary &&
    (!officeLocationsLoadedProductType ||
      officeLocationsLoadedProductType !== APP_PRODUCT_TYPE.CROP) &&
    !officeLocationsLoading &&
    !officeLocationsError;

  useEffect(() => {
    if (shouldLoadOfficeLocations) {
      loadOfficeLocations(APP_PRODUCT_TYPE.CROP);
    }
  }, [loadOfficeLocations, shouldLoadOfficeLocations]);

  // TODO: Refactor the fields so that we don't duplicate the fields in the useOrderDetailsFieldsCrop.tsx and useOrderDetailsFields.tsx
  return useMemo(
    () => [
      {
        title: t('accordions.customerDetails'),
        fields: [
          {
            label: t('fields.accountName'),
            accessor: 'customer.customerName',
          },
          {
            label: t('fields.customer'),
            accessor: 'customer.customerReference',
          },
          {
            label: t('fields.multiAccount'),
            accessor: 'multiAccount',
            hidden: ({ multiAccount }: IGenericObject) => !multiAccount,
          },
          {
            label: t('fields.officeLocation'),
            accessor: isSummary ? 'officeLocation' : 'officeLocationName',
            displayValue: (value) => (isSummary ? displayOfficeLocation(value) : value),
          },
          {
            label: t('fields.transportMethod'),
            accessor: 'transportMethod',
            displayValue: (value) => displayTransportMethod(value),
          },
          {
            label: ({ transportMethod = '' }) => t(`fields.deliveryDate${transportMethod}`),
            accessor: 'startDate',
            displayValue: (value) => displayDate(value),
          },
          {
            label: ({ transportMethod = '' }) => t(`fields.deliveryAddress${transportMethod}`),
            accessor: 'addressName',
            displayValue: (value, { addressData }: IGenericObject) =>
              value || displayAddress(addressData),
          },
          {
            label: t('fields.customerNotes'),
            accessor: 'customerNotes',
          },
        ],
      },
      {
        title: t('accordions.orderDetails'),
        fields: [
          {
            label: t('fields.accountRepresentativeName'),
            accessor: 'representative.representativeName',
          },
          {
            label: t('fields.accountRepresentativeCode'),
            accessor: 'representative.representativeCode',
          },
          {
            label: t('fields.accountRepresentativeEmail'),
            accessor: 'representative.representativeEmail',
          },
          {
            label: t('fields.createdBy'),
            accessor: 'user.userName',
            hidden: isSummary,
          },
          {
            label: t('fields.createdByEmail'),
            accessor: 'user.userEmail',
            hidden: isSummary,
          },
        ],
      },
    ],
    [t, isSummary, displayOfficeLocation, displayTransportMethod],
  );
}
